import React from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { motion } from 'framer-motion';
import MedicationGalaxy from './MedicationGalaxy';
import TaperingDescentChart from './TaperingDescentChart';

const DeprescribingCockpit = ({ patientData, results, onBack }) => {
  const panelVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.96 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  const medicationAnalyses = results?.medication_analyses || [];
  const drugInteractions = results?.drug_interactions || [];

  const severityWeightMap = {
    major: 10,
    moderate: 4,
    minor: 1,
  };

  const interactionPriorityBonus = (interaction) => {
    const text = [
      interaction?.description,
      interaction?.mechanism,
      interaction?.clinical_interpretation,
      interaction?.management_guidance,
      interaction?.monitoring_guidance,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const bonuses = [
      ['bleed', 8],
      ['hemorrhag', 8],
      ['hypogly', 7],
      ['aki', 7],
      ['acute kidney', 7],
      ['renal failure', 7],
      ['qt', 6],
      ['torsad', 6],
    ];

    return bonuses.reduce(
      (maxBonus, [keyword, value]) =>
        text.includes(keyword) ? Math.max(maxBonus, value) : maxBonus,
      0
    );
  };

  const sourceWeightMap = {
    DDInter: 2,
  };

  const drugInteractionScores = {};

  if (Array.isArray(drugInteractions)) {
    drugInteractions.forEach((interaction) => {
      const severityLabel = interaction.severity || '';
      const severityKey = severityLabel.toLowerCase();
      const severityWeight = severityWeightMap[severityKey] || 1;
      const sourceWeight = sourceWeightMap[interaction.source] || 1;
      const confidenceFactor =
        typeof interaction.confidence_score === 'number'
          ? interaction.confidence_score
          : 1;

      const score =
        (severityWeight + interactionPriorityBonus(interaction)) *
        sourceWeight *
        confidenceFactor;
      const drugs = [interaction.drug_a, interaction.drug_b].filter(Boolean);

      drugs.forEach((name) => {
        drugInteractionScores[name] = (drugInteractionScores[name] || 0) + score;
      });
    });
  }

  const priorityMedications = Array.isArray(medicationAnalyses)
    ? [...medicationAnalyses].sort((a, b) => {
        const categoryRank = (category) => {
          if (category === 'RED') return 3;
          if (category === 'YELLOW') return 2;
          if (category === 'GREEN') return 1;
          return 0;
        };

        const extractStats = (analysis) => {
          const flags = analysis.flags || [];
          let majorCount = 0;
          let moderateCount = 0;
          let clinicalPriorityCount = 0;

          flags.forEach((flag) => {
            const text = String(flag);
            if (text.includes('Interaction with') && text.includes('(Major)')) {
              majorCount += 1;
            } else if (
              text.includes('Interaction with') &&
              text.includes('(Moderate)')
            ) {
              moderateCount += 1;
            }
            if (/bleed|hemorrhag|hypogly|aki|acute kidney|qt|torsad/i.test(text)) {
              clinicalPriorityCount += 1;
            }
          });

          const totalFlags = flags.length;
          const interactionScore = drugInteractionScores[analysis.name] || 0;
          const riskScore =
            typeof analysis.risk_score === 'number' ? analysis.risk_score : 0;

          return {
            catRank: categoryRank(analysis.risk_category),
            clinicalPriorityCount,
            majorCount,
            moderateCount,
            totalFlags,
            compositeScore: interactionScore + 0.5 * riskScore,
          };
        };

        const aStats = extractStats(a);
        const bStats = extractStats(b);

        const keyA = [
          aStats.majorCount,
          aStats.clinicalPriorityCount,
          aStats.catRank,
          aStats.moderateCount,
          aStats.totalFlags,
          aStats.compositeScore,
        ];
        const keyB = [
          bStats.majorCount,
          bStats.clinicalPriorityCount,
          bStats.catRank,
          bStats.moderateCount,
          bStats.totalFlags,
          bStats.compositeScore,
        ];

        for (let i = 0; i < keyA.length; i += 1) {
          if (keyA[i] !== keyB[i]) {
            return keyB[i] - keyA[i];
          }
        }
        return 0;
      })
    : [];

  const topPriorityMedications = priorityMedications.slice(0, 3);
  const topPriorityNames = new Set(topPriorityMedications.map((m) => m.name));

  const galaxyInteractions = Array.isArray(drugInteractions)
    ? drugInteractions.filter(
        (interaction) =>
          topPriorityNames.has(interaction.drug_a) ||
          topPriorityNames.has(interaction.drug_b)
      )
    : [];

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center py-10 px-4 lg:px-10 bg-neo-bg"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.9, ease: 'easeOut' }}
    >
      <div className="w-full max-w-7xl space-y-8">
        <motion.div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4"
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack}
                className="w-10 h-10 rounded-lg bg-white border-2 border-neo-main shadow-neo-brutal flex items-center justify-center hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-neo-sm transition-all duration-200 group"
                title="Back to Dashboard"
              >
                <FaArrowLeft className="text-neo-main group-hover:scale-110 transition-transform" />
              </button>
              <p className="text-xs font-black uppercase tracking-widest text-neo-text-sub pt-1">
                Deprescribing Cockpit
              </p>
            </div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight text-neo-main uppercase">
              Bio-Digital Clinical Control
            </h1>
            <p className="text-sm md:text-base text-neo-text font-medium max-w-xl">
              A focused cockpit for monitoring vitals, exploring tapering strategies, and mapping
              interaction risk in one continuous field of view.
            </p>
          </div>
          <motion.div
            className="flex items-center gap-3 text-xs font-bold"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border-2 border-neo-main shadow-neo-sm text-neo-main">
              <span className="w-2 h-2 rounded-full bg-neo-success animate-pulse border border-neo-main" />
              Live Clinical Canvas
            </span>
            <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neo-main text-white border-2 border-neo-main shadow-neo-sm">
              <span className="w-2 h-2 rounded-full bg-neo-accent border border-white" />
              Bio-Digital Mode
            </span>
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.section
            className="flex flex-col"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
          >
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-black tracking-wide text-neo-main uppercase">
                The Tapering Lab
              </h2>
              <span className="text-xs font-bold uppercase tracking-widest text-neo-text-sub">
                Experiment Zone
              </span>
            </header>
            <TaperingDescentChart />
          </motion.section>

          <motion.section
            className="flex flex-col"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.7, delay: 0.32, ease: 'easeOut' }}
          >
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-black tracking-wide text-neo-main uppercase">
                Interaction Galaxy
              </h2>
              <span className="text-xs font-bold uppercase tracking-widest text-neo-text-sub">
                Node Space
              </span>
            </header>
            <MedicationGalaxy
              patientData={patientData}
              drugInteractions={galaxyInteractions}
            />
          </motion.section>
        </div>
      </div>
    </motion.div>
  );
};

export default DeprescribingCockpit;

